import { ApplicationInsights, ITelemetryItem } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';

import { TelemetryInfo } from '../../Config';

// Browser App Insights — replaces Sentry. Telemetry lands in the per-environment
// hub Log Analytics workspace (same place the backend now writes), giving true
// front-end + back-end consolidation. Import `reactPlugin` for withAITracking /
// AppInsightsErrorBoundary, and `appInsights` for trackException / trackTrace.
export const reactPlugin = new ReactPlugin();

export const appInsights = new ApplicationInsights({
    config: {
        connectionString: TelemetryInfo.ConnectionString,
        extensions: [reactPlugin],
        enableAutoRouteTracking: true,
        autoTrackPageVisitTime: true,
        disableFetchTracking: false,
        enableCorsCorrelation: true,
        enableRequestHeaderTracking: true,
        enableResponseHeaderTracking: true,
    },
});

// Mirrors the old Sentry beforeSend/ignoreErrors drop-list: expected/noisy browser
// errors are dropped so they don't pollute the workspace.
const dropPatterns: RegExp[] = [
    /Cannot read properties of undefined \(reading 'status'\)/i,
    /undefined is not an object \(evaluating 'e\.status'\)/i,
    /Loading chunk/i,
    /Loading CSS chunk/i,
    /BrowserAuthError/i,
    /ClientAuthError/i,
    /Network Error/i,
    /CanceledError/i,
    /interaction_required/i,
];

/** Initialize App Insights once, at app startup (replaces initSentry()). */
export function initTelemetry(release: string, environment: string, sessionId: string): void {
    // No-op if the connection string isn't configured for this build/env — safe.
    if (!TelemetryInfo.ConnectionString) {
        return;
    }

    appInsights.loadAppInsights();

    appInsights.addTelemetryInitializer((item: ITelemetryItem) => {
        item.data = item.data || {};
        item.data.release = release;
        item.data.environment = environment;
        item.data.sessionId = sessionId;

        const baseData = item.baseData ?? {};
        const message = String(baseData.message ?? baseData.exceptions?.[0]?.message ?? '');
        if (message && dropPatterns.some((r) => r.test(message))) {
            return false; // drop
        }
        return true;
    });
}

/** Replaces Sentry.setUser({ email }). */
export function setTelemetryUser(email: string): void {
    if (!TelemetryInfo.ConnectionString || !email) {
        return;
    }
    appInsights.setAuthenticatedUserContext(email);
}
